import h from 'hyperscript';

import arrowDownIcon from '@disy/cadenza-icons/arrow-down.svg';
import arrowUpIcon from '@disy/cadenza-icons/arrow-up.svg';
import { isAbortError } from '@disy/lib/abort-error';
import { isChromiumBrowser } from '@disy/lib/browser';
import { assert, requireNonNullable } from '@disy/lib/custom-error';
import { debounce } from '@disy/lib/debounce';
import { isTest } from '@disy/lib/env';
import { getTabbables, on } from '@disy/lib/event-util';
import { compareWithLocale } from '@disy/lib/locale';
import { getLogger } from '@disy/lib/logging';
import type { LazyInit } from '@disy/lib/promise-utils';
import { lazy } from '@disy/lib/promise-utils';
import { scrollIntoView } from '@disy/lib/scroll-into-view';
import type { CancelableApiCallback } from '@disy/lib/single-execution';
import { frontendSingleExecution } from '@disy/lib/single-execution';
import { type Teardown, tearDown } from '@disy/lib/teardown';
import { uniqueId } from '@disy/lib/unique-id';
import type {
  DragAndDropManualOrderingConfig,
  MoveToData,
} from '@disy/ui.responsive-table/add-drag-and-drop-column-for-manual-ordering-of-rows';
import { addDragAndDropColumnForManualOrdering } from '@disy/ui.responsive-table/add-drag-and-drop-column-for-manual-ordering-of-rows';
import { SELECTION_CHECKBOX_CLASS, TableEvents } from '@disy/ui.responsive-table/responsive-table-constants';
import { ResponsiveTableKeyboardEventHandler } from '@disy/ui.responsive-table/responsive-table-keyboard-handler';
import type { OrderDirection } from '@disy/ui.responsive-table/responsive-table-manual-ordering';
import {
  type ToolbarManualOrderingConfig,
  addOrderingButtons,
} from '@disy/ui.responsive-table/responsive-table-manual-ordering';
import type { ColumnResponsiveBehavior } from '@disy/ui.responsive-table/responsive-table-resize-handler';
import { ResponsiveTableResizeHandler } from '@disy/ui.responsive-table/responsive-table-resize-handler';
import type { RowRenderer } from '@disy/ui.responsive-table/responsive-table-row-renderer';
import { defaultRowRenderer } from '@disy/ui.responsive-table/responsive-table-row-renderer';
import type { ResponsiveTableToolbarOptions } from '@disy/ui.responsive-table/responsive-table-toolbar';
import { ResponsiveTableToolbar } from '@disy/ui.responsive-table/responsive-table-toolbar';
import { attachGroupData, getRowData } from '@disy/ui.responsive-table/responsive-table-utils';
import type { AlertType } from '@disy/ui/alert';
import { Alert } from '@disy/ui/alert';
import { createButton } from '@disy/ui/button';
import { type Content, normalizeContent } from '@disy/ui/content';
import { icon } from '@disy/ui/icon';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_CONDITIONAL_FOOTER,
  type PaginationControl,
} from '@disy/ui/pagination-control';
import type { ChangePageSizeEvent } from '@disy/ui/pagination-control-page-size-picker';
import type { ChangePageEvent } from '@disy/ui/pagination-control-stepper';
import { ProgressSpinner } from '@disy/ui/progress-spinner';
import '@disy/ui/search-input';
import type { SearchInput } from '@disy/ui/search-input';

import { toggleDarkLight } from './responsive-table-utils';
import './responsive-table.css';
import i18n from './responsive-table.properties';

const SORT_BUTTON_CLASS = 'd-responsive-table--sort-button';

const HIGHLIGHTED_ROW_CLASS = 'is-highlighted';
const ROW_CLASS = 'focus-outline-keynav';
const logger = getLogger('ui/responsive-table');

export interface CellRendererContext<T> {
  /** The item data of the row */
  rowData: T;
  /** Row index */
  rowIndex: number;
  /** The total number of rows */
  rowCount: number;
  /** The table row element in which the cell is rendered */
  tr: HTMLTableRowElement;
  /** The table element in which the cell is rendered */
  table: HTMLTableElement;
  /** ID, used to identify the heading of the row in labels */
  rowHeadingId: string;
}

export interface CellRenderer<T> {
  /**
   * @param data - The cell data
   * @param context - Rendering context
   * @return The rendered cell content (or undefined if there is nothing in the cell)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (data: any, context: CellRendererContext<T>): Content | undefined;
}

export type ColumnKey<T> = Exclude<keyof T, number | symbol>;
type ColumnSort = 'ascending' | 'descending' | 'disabled';
export type ColumnWidth = number | string;

export interface Column<T> {
  /**
   * Whether the column is the main column
   *
   * Only one column can be the main column. The main column is highlighted and by default sorted ascending.
   */
  main?: boolean;
  /** The column name, used in the column header */
  name?: Content;
  /** The key in the row data objects to get the cell data from */
  key: ColumnKey<T>;
  /** The cell renderer: By default, the string value of the cell data is used. */
  renderer?: CellRenderer<T>;
  /**
   * The column width (of the content box): a CSS length in pixels like '100px'
   * or a whole number which is used as ratio for dividing remaining free space.
   * Default is 1
   */
  width?: ColumnWidth;
  /**
   * Used to determine width required for columns which are defined in ratios when resizing table.
   * If not provided, the default value of 200px will be considered. This will not enforce minimum
   * width on column, the column may still be visually less than value defined here.
   * This is used only in calculation to determine whether table has enough width to fit all columns.
   */
  resizingRequiredMinWidth?: string;
  /** Whether to enable sorting of this column and in which order */
  sort?: ColumnSort | boolean;
  /**
   * Defines whether the column will wrap around or be hidden when screen size has not enough width to layout whole
   * table without horizontal scrolling.
   */
  responsiveBehavior?: ColumnResponsiveBehavior;
  /** The column alignment */
  align?: 'left' | 'center' | 'right';
  /** Whether column contents get truncated or wrapped. If undefined, `createCell` will default to `true`. */
  ellipsis?: boolean;
}

export interface SortOrder<T> {
  /** The key of the column to sort */
  orderByKey: ColumnKey<T>;
  /** The sort direction */
  order: 'ascending' | 'descending';
}

export interface DataProviderOptions<T> {
  /** Offset to request table rows from */
  offset: number;
  /** Number of table rows to request */
  limit: number;
  /** An optional sort order */
  sortOrder?: SortOrder<T>;
  /**
   * Signals whether the data loading was aborted, because the table was refreshed
   * while data was still being loaded. The `DataProvider` should reject the returned
   * `Promise` with an `AbortError` in this case.
   */
  signal?: AbortSignal;
}

export interface Data<T> {
  /** The number of table rows, disregarding pagination and filtering */
  rowCountWithoutFilter: number;
  /** The number of table rows, disregarding pagination */
  rowCount: number;
  /** The requested table rows */
  rows: T[];
}

type GroupFunction<T, G = unknown> = (rowData: T) => G;
type GroupHeaderFunction<T, G = unknown> = (groupValue: G, rowData: T) => Content;

export interface ToolbarConfig extends Omit<ResponsiveTableToolbarOptions, 'searchInput'> {
  showToolbar?: boolean;
}

export interface DataProvider<T> {
  (options: DataProviderOptions<T>): Promise<Data<T>>;
}

export const DataProvider = {
  /**
   * Creates a static {@link DataProvider} instance serving the given rows.
   *
   * @param allRowsInit - The rows or a function returning them
   * @param [options]
   * @param [options.filter] - A row filter predicate
   * @param [options.compare] - A row comparator function, used for sorting
   * @return The static `DataProvider` instance
   */
  staticData<T>(
    allRowsInit: LazyInit<T[]>,
    {
      filter,
      compare = (a, b, { orderByKey, order }) => defaultCompare(a[orderByKey], b[orderByKey], order),
    }: { filter?: Predicate<T>; compare?: Comparator<T> } = {},
  ): DataProvider<T> {
    const allRows = lazy(allRowsInit, { once: false });
    return async ({ offset, limit, sortOrder }: DataProviderOptions<T>) => {
      let resultRows = await allRows();
      const rowCountWithoutFilter = resultRows.length;
      if (filter) {
        resultRows = resultRows.filter(filter);
      }
      if (sortOrder) {
        // Copy the array so that sort() does not modify the original.
        resultRows = [...resultRows].sort((a, b) => compare(a, b, sortOrder));
      }
      return {
        rowCountWithoutFilter,
        rowCount: resultRows.length,
        rows: resultRows.slice(offset, offset + limit),
      };
    };
  },
} as const;

interface Predicate<T> {
  (value: T): boolean;
}

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description
 */
interface Comparator<T> {
  (a: T, b: T, sortOrder: SortOrder<T>): number;
}

export type SelectionMode = 'single' | 'multiple' | 'none';

const IS_FIXED_WIDTH_IN_PX_CHECKER = new RegExp('d*px');
export const ROW_GROUP_HEADER_CLASS = 'd-responsive-table--group-header';

function createSelectionHint() {
  return h('span', { id: uniqueId(), hidden: true }, i18n('selection.one'));
}

function createSelectionHeaderCheckbox() {
  return h(`input.${SELECTION_CHECKBOX_CLASS}`, {
    type: 'checkbox',
    title: i18n('selection.all'),
    attrs: { 'aria-label': i18n('selection.all') },
  });
}

export class ResponsiveTable<T> extends HTMLElement {
  #singleExecutionRefresh: CancelableApiCallback<void>;

  readonly #columns: Column<T>[];
  readonly #rowRenderer;
  readonly #data;
  readonly #noResultsMessage;
  readonly #groupingConfig?: { group: GroupFunction<T>; groupHeader: GroupHeaderFunction<T> };

  readonly #table: HTMLTableElement;
  readonly #sortHint: HTMLElement;
  #selectionHeaderCheckbox?: HTMLInputElement; // checkbox in the table header that can be used to multi-select rows

  readonly #pagination: HTMLElement;
  readonly #paginationControl: PaginationControl;

  readonly #toolbar?: ResponsiveTableToolbar;
  readonly #loadingOverlay: HTMLElement;
  #alert?: Alert;

  #resizeHandler: ResponsiveTableResizeHandler;
  readonly #paginationEnabled: boolean;
  readonly #paginationAlwaysVisible: boolean;
  readonly #stickyHeader: boolean;
  #stickyElements: HTMLElement[] = [];
  #stickyBackgroundLayer?: HTMLElement;
  #pageSizeInternal: number;
  #teardowns: Teardown[] = [];

  /**
   * @param options
   * @param options.columns
   * @param options.data
   * @param options.selectionMode - The selection mode ('none'|'single'|'multiple')
   *   _Note:_ Multiple selection requires a main column. Because the checkbox is aria-labelled with the
   *   text of the cell in the main column so that it reads as "Select <value>".
   *   It also needs the showHeader active, because the selectAll checkbox needs to be rendered there.
   * @param options.showHeader - Whether to show the table header
   * @param options.noResultsMessage - Customize the message to show when the table is empty.
   * If it's Alert, then alert itself is shown. If it's string or any other Node, then it's contained inside info alert.
   * If it's a function returning a string or Node then it's executed and the result is shown.
   * @param options.rowRenderer - Custom row renderer for advanced customization
   * @param options.pagination - Whether pagination should be enabled
   * @param options.paginationAlwaysVisible - Whether pagination should always be shown, even when there are less than DEFAULT_PAGE_SIZE_CONDITIONAL_FOOTER rows.
   * @param options.paginationConfigurable - Whether pagination row-count per page can be changed for this table
   * @param options.paginationPageSize - Pagination row-count per page
   * @param options.keyForSession - A string to use for persisting data for the session
   * @param options.filterFunction - If provided, inline search input will be shown in table toolbar.
   * Provided function is a predicate, which defines if row matches search criteria.
   * @param options.groupingConfig - Configuration for grouping. If provided then for each group additional grouping row will be rendered.
   * We assume that rows are already sorting by grouping key.
   * @param options.variant - Controls the size of table's header
   * @param options.size - Controls the `max-width` of the table
   * @param options.sticky - Whether the header should be sticky
   * @param options.toolbarConfig - Configuration of the toolbar
   * @param options.manualOrderingConfig - Configuration for manual ordering (not: sorting). The toolbarConfig.items
   * must be a function (and not a Promise or static Array) for this to work since a re-rendering of the toolbar is needed.
   * @param options.manualOrderingConfig2 - Configuration for manual ordering (not: sorting). Controls are in row only.
   *   Naming is temporary, as it will most probably replace the toolbar sorting options configured by the option
   *   manualOrderingConfig.
   */
  constructor({
    columns,
    rowRenderer = defaultRowRenderer,
    data,
    selectionMode = 'none',
    showHeader = true,
    toolbarConfig = { showRowCount: true },
    toolbarManualOrderingConfig,
    dragAndDropManualOrderingConfig,
    noResultsMessage = i18n('noResults'),
    pagination = true,
    paginationAlwaysVisible = false,
    paginationConfigurable = false,
    paginationPageSize,
    keyForSession,
    filterFunction,
    groupingConfig,
    size = 'flex',
    variant,
    sticky = false,
    initialSortOrder,
    highlightMainColumn = true,
  }: {
    columns: Column<T>[];
    rowRenderer?: RowRenderer<T>;
    data: DataProvider<T>;
    selectionMode?: SelectionMode;
    showHeader?: boolean;
    toolbarConfig?: ToolbarConfig;
    toolbarManualOrderingConfig?: ToolbarManualOrderingConfig<T>;
    dragAndDropManualOrderingConfig?: DragAndDropManualOrderingConfig<T>;
    noResultsMessage?: Content | (() => Content);
    pagination?: boolean;
    paginationAlwaysVisible?: boolean;
    paginationConfigurable?: boolean;
    paginationPageSize?: number;
    keyForSession?: string;
    filterFunction?: (searchPhrase: string, row: T) => boolean;
    groupingConfig?: {
      /** A key or function to get the grouping value from the row data */
      group: ColumnKey<T> | GroupFunction<T>;
      /** A key or function to get the header of a group from the row data */
      groupHeader?: ColumnKey<T> | GroupHeaderFunction<T>;
    };
    variant?: 'light';
    size?: 'flex' | 's' | 'm' | 'l' | 'xl';
    sticky?: boolean;
    initialSortOrder?: SortOrder<T> | ColumnKey<T>;
    highlightMainColumn?: boolean;
  }) {
    super();
    assert(columns.filter((c) => c.main).length <= 1, 'Only one column can be the main column');
    assert(selectionMode !== 'multiple' || columns.some((c) => c.main), 'SelectionMode requires a main column');
    assert(selectionMode !== 'multiple' || showHeader, 'SelectionMode requires a header');
    assert(
      !dragAndDropManualOrderingConfig || !(pagination || columns.some(isColumnSortable)),
      'Manual ordering is incompatible with pagination and sortable columns', // Pagination support just not implemented
    );
    assertColumnWidths(columns);
    this.#assertManualOrderApplicable(columns, pagination, toolbarManualOrderingConfig);
    if (showHeader) {
      assert(
        columns.filter((c) => c.name !== undefined).length === columns.length,
        'Column name is required if showHeader=true',
      );
    }
    if (variant) {
      this.classList.add(`d-responsive-table-${variant}`);
    }
    this.#stickyHeader = sticky;
    this.classList.toggle('highlight-main-column', highlightMainColumn);
    this.dataset.selectionMode = selectionMode;
    if (toolbarConfig.showToolbar === undefined) {
      toolbarConfig.showToolbar = toolbarConfig.heading != null;
    }

    this.#columns = [...columns]; // Clone the array, because we will add additional columns
    this.#initializeSelection();
    this.#columns = addDragAndDropColumnForManualOrdering<T>(
      this,
      this.#columns,
      (moveToData) => this.#moveRows(moveToData),
      dragAndDropManualOrderingConfig,
    );
    this.#rowRenderer = rowRenderer;
    let searchInput: SearchInput | undefined;
    if (filterFunction && toolbarConfig.showToolbar) {
      const fetch: LazyInit<T[]> = () => {
        return data({ limit: Number.MAX_SAFE_INTEGER, offset: 0, sortOrder: this.#sortOrder }).then(
          (result) => result.rows,
        );
      };
      // fetch all rows and then on top of that apply filter from toolbar
      searchInput = h<SearchInput>('d-search-input.filter-control', { name: 'text' });

      this.#data = DataProvider.staticData(fetch, {
        filter: (row) => filterFunction(searchInput!.value, row),
      });
    } else {
      this.#data = data;
    }
    this.#noResultsMessage = noResultsMessage;

    if (groupingConfig) {
      const { group, groupHeader = (groupValue) => String(groupValue) } = groupingConfig;
      this.#groupingConfig = {
        group: typeof group === 'function' ? group : (rowData) => rowData[group],
        groupHeader: typeof groupHeader === 'function' ? groupHeader : (_, rowData) => String(rowData[groupHeader]),
      };
    }

    // https://adrianroselli.com/2021/04/sortable-table-columns.html#HintLabel
    this.#sortHint = h('span', { id: uniqueId(), hidden: true }, i18n('aria.sort'));

    const tHead =
      showHeader ?
        h('thead.sticky', { attrs: { role: 'rowgroup' } }, [
          h(
            `tr.${ROW_CLASS}.light-bg`,
            this.#columns.map((column) => this.#createColumnHeader(column)),
          ),
        ])
      : null;
    const tBody = h('tbody', { attrs: { role: 'rowgroup' } });

    this.#table = h(
      'table',
      {
        attrs: { role: 'grid' },
        id: uniqueId(),
      },
      [tHead, tBody],
    );

    this.#paginationEnabled = pagination;
    this.#paginationAlwaysVisible = paginationAlwaysVisible;
    this.#pageSizeInternal =
      paginationAlwaysVisible ?
        (paginationPageSize ?? DEFAULT_PAGE_SIZE)
      : (paginationPageSize ?? DEFAULT_PAGE_SIZE_CONDITIONAL_FOOTER);
    const changePageListener = ({ detail: { delta } }: ChangePageEvent) => delta && this.#turnPage(delta);
    this.#paginationControl = h('d-pagination-control', {
      attrs: {
        'page-size': this.#pageSize.toString(),
        'row-offset': this.#rowOffset.toString(),
        'row-count': this.rowCount.toString(),
        'aria-controls': this.#table.id,
        ...(keyForSession ? { 'key-for-session': keyForSession } : null),
        ...(paginationConfigurable ? { configurable: '' } : null),
      },
      onchangePageSize: ({ detail: { pageSize } }: ChangePageSizeEvent) => {
        if (pageSize && pageSize !== this.#pageSize) {
          this.#pageSize = pageSize;
          this.#turnPage(0);
        }
      },
      onchangePage: changePageListener,
    });
    // update the row limit from the PaginationControl if it comes from the session
    this.#pageSizeInternal = this.#paginationControl.pageSize;

    this.#pagination = h('footer.d-pagination.d-stack-h.space-1.light-bg', { hidden: true }, [this.#paginationControl]);

    if (toolbarConfig.showToolbar) {
      toolbarConfig.paginationStepperAriaControls = this.#paginationControl.ariaControls;
      this.#toolbar = new ResponsiveTableToolbar(this, {
        searchInput,
        ...(toolbarManualOrderingConfig ?
          addOrderingButtons(
            toolbarConfig,
            toolbarManualOrderingConfig,
            this,
            (selectedIndex, nextIndex, orderDirection) =>
              this.#reorderTableRowVisually(selectedIndex, nextIndex, orderDirection),
            (selectedIndex: number) => this.#canReorder(selectedIndex),
          )
        : toolbarConfig),
      });
      on(this.#toolbar, 'changePage', changePageListener);
      this.#toolbar.classList.add('sticky');
    }
    this.#loadingOverlay = h('.d-responsive-table--loading-overlay', [
      new ProgressSpinner({ label: i18n('aria.loading'), labelVisible: false }),
    ]);

    if (tHead) {
      on(
        tHead,
        'click',
        (event) => this.#updateSortOrder(event.delegateTarget as HTMLButtonElement), // Sort button is always in a <th> element
        { delegate: `.${SORT_BUTTON_CLASS}` },
      );
    }

    const keyboardEventHandler = new ResponsiveTableKeyboardEventHandler(this.#table, selectionMode);
    on(this.#table, 'keydown', keyboardEventHandler);
    on(this, TableEvents.REFRESH, keyboardEventHandler);

    this.#resizeHandler = new ResponsiveTableResizeHandler(
      this,
      this.#table,
      this.#columns,
      this.selectionMode === 'multiple',
    );
    on(this, TableEvents.REFRESH, this.#resizeHandler);

    // _refresh() should run only once at a time
    this.#singleExecutionRefresh = frontendSingleExecution(this.#refresh);

    if (size !== 'flex') {
      this.classList.add(`d-responsive-table-${size}`);
    }

    if (initialSortOrder) {
      if (typeof initialSortOrder !== 'object') {
        initialSortOrder = { orderByKey: initialSortOrder, order: 'ascending' };
      }
      this.#sortOrder = initialSortOrder;
    }
  }

  #createColumnHeader(column: Column<T>) {
    const {
      main = false,
      key,
      name = '', // The actions column does not have a name
      align = 'left',
    } = column;
    const columnHeader = h('th', { scope: 'column', 'data-key': key, role: 'columnheader' });
    columnHeader.classList.toggle('is-main-column', main);

    if (align !== 'left') {
      columnHeader.classList.add(`align-${align}`);
    }

    const sort = getColumnSort(column);
    if (isSortable(sort)) {
      let sortButton = this.#createSortButton(name);
      if (main) {
        columnHeader.setAttribute('aria-sort', String(sort));
        sortButton.title = i18n(`sorting[${sort}]`);
      }
      columnHeader.append(sortButton);
    } else {
      columnHeader.append(...normalizeContent(name));
    }

    return columnHeader;
  }

  #createSortButton(name: Content) {
    const sortButtonId = uniqueId();
    return createButton(
      [
        ...normalizeContent(name),
        icon(arrowUpIcon, { styleClass: 'd-responsive-table--icon-ascending' }),
        icon(arrowDownIcon, { styleClass: 'd-responsive-table--icon-descending' }),
      ],
      {
        styleClass: SORT_BUTTON_CLASS,
        variant: 'borderless',
        size: 'xs',
        id: sortButtonId,
        title: i18n('sorting'),
        attrs: { 'aria-labelledby': `${sortButtonId} ${this.#sortHint.id}` },
      },
    );
  }

  #turnPage(delta: number) {
    return this.#loadData({ offset: Math.max(0, this.#rowOffset + delta * this.#pageSize) });
  }

  loadPage(page: number) {
    return this.#loadData({ offset: page * this.#pageSize });
  }

  connectedCallback() {
    this.classList.add(
      'd-responsive-table',
      // links within the table are styled differently, see anchor.css
      'd-anchor-light-context',
    );
    this.setAttribute('role', 'region');
    this.#resizeHandler.initialize();
    this.append(this.#table, this.#pagination, this.#loadingOverlay, this.#sortHint);
    if (this.#toolbar) {
      this.prepend(this.#toolbar);
    }
    this.refresh();
    if (this.#stickyHeader) {
      this.#setupStickyHeader();
    }
  }

  #setupStickyHeader() {
    const scrollParent = findScrollParent(this);
    this.#teardowns.push(
      on(scrollParent, 'scroll', () => {
        // for multiple tabs, only set up the sticky element once if the table is currently visible
        if (!this.checkVisibility()) {
          return;
        }
        if (this.#stickyElements.length === 0) {
          this.#configureStickyElements();
        }
        window.requestAnimationFrame(() => {
          const bottomMostElement = this.#stickyElements.at(-1)!;
          const stickyTopPosition = [...this.#stickyElements].reduce((acc, curr) => acc + curr.clientHeight, 0);
          const actualBackgroundBottom = this.#backgroundBottomFromStickyTop(
            stickyTopPosition,
            scrollParent.getBoundingClientRect().y,
          );
          this.#stickyBackgroundLayer!.hidden = bottomMostElement.getBoundingClientRect().top > actualBackgroundBottom;
        });
      }),
    );
  }

  #configureStickyElements() {
    this.#stickyElements = [...(this.parentElement?.querySelectorAll<HTMLElement>('.sticky') ?? [])];
    // sort by current absolute position
    this.#stickyElements.sort((a, b) => a.clientTop - b.clientTop);
    // add sibling elements
    // set top to the sum of the heights of the previous elements.
    let stickyTopPosition = 0; // px
    for (const el of this.#stickyElements) {
      el.style.top = `${stickyTopPosition}px`;
      el.style.zIndex = '1';
      stickyTopPosition += el.clientHeight;
    }
    this.#stickyBackgroundLayer = h<HTMLElement>('.d-responsive-table--sticky-background');
    const scrollParent = findScrollParent(this);
    scrollParent.insertAdjacentElement('afterbegin', this.#stickyBackgroundLayer);
    this.#stickyBackgroundLayer.hidden = true;
    // we only need the background layer once all elements become
    // sticky, so we just choose the final size
    if (scrollParent.classList.contains('d-page--content')) {
      this.#stickyBackgroundLayer.style.height = `calc(${stickyTopPosition}px + var(--default-page-margin))`;
    } else {
      this.#stickyBackgroundLayer.style.height = `${stickyTopPosition}px`;
    }
    this.#stickyBackgroundLayer.style.top = `${scrollParent.getBoundingClientRect().y}px`;
  }

  #backgroundBottomFromStickyTop(stickyTopPosition: number, scrollParentY: number) {
    return stickyTopPosition + scrollParentY;
  }

  disconnectedCallback() {
    this.#resizeHandler.destroy();
    this.#teardowns = tearDown(this.#teardowns);
  }

  /**
   * Refresh the table, i.e. request the data anew and render them.
   *
   * Call for example when some (external) table row filter changed.
   *
   * @fires ResponsiveTable#refresh
   * @return Refresh call promise
   */
  refresh() {
    return this.#singleExecutionRefresh();
  }

  highlightRows(highlightPredicate: (rowData: T) => boolean) {
    this.rowElements.forEach((row) => row.classList.remove(HIGHLIGHTED_ROW_CLASS));
    // Need a short timeout to let highlighted class be removed first from the DOM
    setTimeout(
      () =>
        this.rowElements.forEach((rowElement) => {
          const highlight = highlightPredicate(getRowData(rowElement));
          rowElement.classList.toggle(HIGHLIGHTED_ROW_CLASS, highlight);
        }),
      50,
    );
  }

  selectRows(selectPredicate: (rowData: T) => boolean) {
    const selectionMode = this.selectionMode;
    if (selectionMode === 'none') {
      return;
    }
    const selectedRowElements = this.rowElements.filter((tr) => {
      const select = selectPredicate(getRowData(tr)) && !isRowDisabled(tr);
      setRowSelected(tr, select);
      return select;
    });
    if (selectedRowElements.length === 1) {
      scrollIntoView(selectedRowElements[0]);
    }
    this.#toolbar?.refresh();
  }

  #refresh(signal?: AbortSignal) {
    return this.#loadData({ signal });
  }

  async #loadData({
    offset = this.#rowOffset,
    limit = this.#pageSize,
    sortOrder = this.#sortOrder,
    signal,
  }: {
    offset?: number;
    limit?: number;
    sortOrder?: SortOrder<T>;
    signal?: AbortSignal;
  }) {
    const debouncedShowLoading = this.#showLoadingOverlayDebounced();
    this.#resetAlert();
    logger.debug(`Loading ${limit} rows starting from index ${offset}`);

    try {
      const options = { offset, limit, sortOrder, signal };
      this.#renderData(await this.#data(options), offset);
    } catch (error) {
      if (!isAbortError(error)) {
        logger.error('Failed to load data', error);
        this.#setAlert('error', i18n('error'), createButton(i18n('retry'), { onclick: () => this.refresh() }));
        throw error;
      }
    } finally {
      debouncedShowLoading.cancel();
      this.#loadingOverlay.hidden = true;
    }
  }

  // If loading is very fast, then don't show the loading overlay for a split second
  #showLoadingOverlayDebounced() {
    const debouncedShowLoading = debounce(() => {
      this.#loadingOverlay.hidden = false;
    });
    debouncedShowLoading();
    return debouncedShowLoading;
  }

  #renderData({ rows, rowCount, rowCountWithoutFilter }: Data<T>, rowOffset: number) {
    this.classList.toggle('has-rows', rowCount > 0);
    const tBody = this.#tBody;
    tBody.innerHTML = '';
    let previousGroupValue: unknown;
    const groupingConfig = this.#groupingConfig;
    tBody.append(
      ...rows.flatMap((rowData, localRowIndex) => {
        const groupValue = groupingConfig ? groupingConfig.group(rowData) : undefined;

        const row = this.#createRow(rowOffset + localRowIndex, rowData, groupValue, rowCount);
        if (groupingConfig && previousGroupValue !== groupValue) {
          previousGroupValue = groupValue;
          const tr = h<HTMLTableRowElement>(
            `tr.${ROW_GROUP_HEADER_CLASS}`,
            h('th', { colSpan: this.#columns.length }, groupingConfig.groupHeader(groupValue, rowData)),
          );
          attachGroupData(tr, groupValue);
          return [tr, row];
        }
        return [row];
      }),
    );

    const table = this.#table;
    table.setAttribute('aria-rowcount', String(rowCount));
    table.dataset.rowCountWithoutFilter = String(rowCountWithoutFilter);
    this.#updateSelectionHeaderCheckbox();

    this.#pagination.hidden = !this.#isPaginationVisible(rowCountWithoutFilter, rowOffset);
    this.#paginationControl.setAttribute('row-offset', rowOffset.toString());
    this.#paginationControl.setAttribute('row-count', rowCount.toString());
    this.#paginationControl.setAttribute('page-size', this.#pageSize.toString());
    this.#paginationControl.setAttribute('aria-controls', this.#table.id);
    if (this.#toolbar) {
      this.#toolbar.hidePaginationStepper(this.#pagination.hidden || rowCount === 0);
      const paginationControl = this.#paginationControl;
      this.#toolbar.setAttribute('current-page', paginationControl.currentPage.toString());
      this.#toolbar.setAttribute('page-count', paginationControl.pageCount.toString());
    }

    if (rowCount === 0) {
      this.#setAlert('info', rowCountWithoutFilter > 0 ? i18n('noFilterResults') : this.#noResultsMessage);
    } else {
      this.#resetAlert();
    }

    this.dispatchEvent(new CustomEvent(TableEvents.REFRESH));
  }

  #isPaginationVisible(rowCountWithoutFilter: number, rowOffset: number) {
    if (this.#paginationAlwaysVisible) {
      return true;
    }
    if (!this.#paginationEnabled) {
      return false;
    }
    if (rowCountWithoutFilter <= this.#pageSizeInternal) {
      // If we have 11 elements with #pageSizeInternal=10, then if we remove the last item, we want to
      // still show the footer to let user go to previous page (in that case rowOffset=10 and rowCountWithoutFilter=10)
      return rowOffset === rowCountWithoutFilter;
    }
    return true;
  }

  #createRow(rowIndex: number, rowData: T, groupData: unknown, rowCount: number) {
    const rowHeadingId = uniqueId();
    const rowElement = this.#rowRenderer({
      columns: this.#columns,
      rowIndex,
      rowData,
      groupData,
      rowCount,
      table: this.#table,
      rowHeadingId,
    });

    rowElement.setAttribute('aria-rowindex', String(rowIndex + 1)); // row index is 1-based
    rowElement.classList.add(ROW_CLASS, 'light-bg');

    // set id and scope for row heading
    let rowHeading = rowElement.querySelector('th');
    if (rowHeading) {
      rowHeading.id = rowHeadingId;
      rowHeading.setAttribute('scope', 'row');
    }

    return rowElement;
  }

  #resetAlert() {
    this.#alert?.remove();
    this.#alert = undefined;
  }

  #setAlert(type: AlertType, content: Content | (() => Content), button?: HTMLButtonElement) {
    this.#resetAlert();
    if (content) {
      if (typeof content === 'function') {
        content = content();
      }
      const alert = content instanceof Alert ? content : this.#createAlert(type, content, button);
      this.#alert = alert;
      alert.classList.add('d-responsive-table--message');
      this.append(alert);
    }
  }

  #createAlert(type: AlertType, content: Content, button?: HTMLButtonElement) {
    return h<Alert>(
      `d-alert.d-alert-with-button-bottom.light-bg`,
      { size: 's', type },
      h('.d-alert--text', normalizeContent(content)),
      button,
    );
  }

  get selectionMode() {
    return this.dataset.selectionMode! as SelectionMode; // this.dataset.selectionMode is set in the constructor, it will be defined
  }

  /**
   * @return The _total_ count of table rows (disregarding pagination)
   */
  get rowCount(): number {
    return Number(this.#table.getAttribute('aria-rowcount'));
  }

  get rowCountWithoutFilter(): number {
    return Number(this.#table.dataset.rowCountWithoutFilter);
  }

  get #rowOffset() {
    const firstRow = this.#tBody.querySelector('tr[aria-rowindex]'); // rows that are group headers do not have aria-rowindex
    return firstRow ? Number(firstRow.getAttribute('aria-rowindex')) - 1 : 0; // aria-rowindex is 1-based
  }

  get #pageSize() {
    if (this.#paginationEnabled) {
      return this.#pageSizeInternal;
    }
    return Number.MAX_SAFE_INTEGER;
  }

  set #pageSize(limit: number) {
    if (this.#paginationEnabled) {
      this.#pageSizeInternal = limit;
    }
  }

  get #sortOrder() {
    const sortedColumnHeader = this.#table.querySelector<HTMLTableCellElement>('th[aria-sort]');
    if (sortedColumnHeader) {
      const sortOrder = {
        orderByKey: sortedColumnHeader.dataset.key,
        order: sortedColumnHeader.getAttribute('aria-sort'),
      };
      return sortOrder as SortOrder<T>;
    }
  }

  set #sortOrder(sortOrder) {
    const previousSortOrder = this.#sortOrder;
    if (previousSortOrder?.orderByKey === sortOrder?.orderByKey && previousSortOrder?.order === sortOrder?.order) {
      return;
    }

    if (previousSortOrder) {
      this.#updateColumnSortOrder(previousSortOrder.orderByKey);
    }
    if (sortOrder) {
      this.#updateColumnSortOrder(sortOrder.orderByKey, sortOrder.order);
    }

    this.refresh();
  }

  #updateColumnSortOrder(columnKey: ColumnKey<T>, order?: SortOrder<T>['order']) {
    const columnHeader = this.#table.querySelector(`th[data-key="${columnKey}"]`);
    const sortButton = columnHeader?.querySelector<HTMLElement>(`.${SORT_BUTTON_CLASS}`);
    const isSortable = columnHeader != null && sortButton != null;
    if (isSortable) {
      if (order) {
        columnHeader.setAttribute('aria-sort', order);
        sortButton.title = i18n(`sorting[${order}]`);
      } else {
        columnHeader.removeAttribute('aria-sort');
        sortButton.title = i18n('sorting');
      }
    }
  }

  get selectedRowElements(): HTMLTableRowElement[] {
    return [...this.#tBody.querySelectorAll<HTMLTableRowElement>('tr[aria-selected=true]')];
  }

  get selectedRows(): T[] {
    return this.selectedRowElements.map(getRowData<T>);
  }

  get #tBody() {
    return this.#table.tBodies[0];
  }

  get rowElements() {
    return [...this.#tBody.children] as HTMLTableRowElement[];
  }

  get rowData() {
    return this.rowElements.map(getRowData<T>);
  }

  #updateSelectionHeaderCheckbox() {
    const selectionHeaderCheckbox = this.#selectionHeaderCheckbox;

    if (!selectionHeaderCheckbox) {
      return;
    }

    selectionHeaderCheckbox.disabled = this.#tBody.querySelectorAll('tr[aria-disabled=true]').length === this.rowCount; // Disables header checkbox if no row is selectable

    if (this.selectedRows.length === this.rowCount) {
      selectionHeaderCheckbox.checked = true;
      selectionHeaderCheckbox.indeterminate = false;
    } else if (this.selectedRows.length === 0) {
      selectionHeaderCheckbox.checked = false;
      selectionHeaderCheckbox.indeterminate = false;
    } else {
      selectionHeaderCheckbox.indeterminate = true;
    }
  }

  #updateSortOrder(sortButton: HTMLButtonElement) {
    const columnHeader = sortButton.closest('th')!;
    this.#sortOrder = {
      orderByKey: columnHeader.dataset.key! as ColumnKey<T>,
      order: columnHeader.getAttribute('aria-sort') === 'ascending' ? 'descending' : 'ascending',
    };
  }

  /**
   * When ordering table items manually, it should not reload its data after a change in the
   * order has been made (UX). Therefore, the items need to be reordered accordingly on the client.
   */
  #reorderTableRowVisually(selectedIndex: number, nextIndex: number, orderDirection: OrderDirection) {
    const allRows = this.rowElements;
    const selectedRow = allRows[selectedIndex];
    let nextRow: Element | null = allRows[nextIndex];
    if (nextRow) {
      nextRow.before(selectedRow);
    } else if (nextIndex >= allRows.length) {
      nextRow = selectedRow.nextElementSibling;
      assert(nextRow, 'nextRow must exist.');
      nextRow.after(selectedRow);
    }
    if (this.#stickyBackgroundLayer) {
      selectedRow.style.scrollMarginTop = this.#stickyBackgroundLayer.style.height;

      const scrollParent = findScrollParent(this);
      const stickyBackgroundRect = this.#stickyBackgroundLayer.getBoundingClientRect();
      const stickyBorderBottom = stickyBackgroundRect.y + stickyBackgroundRect.height;
      const selectedRowRect = selectedRow.getBoundingClientRect();

      if (!this.#stickyBackgroundLayer.hidden && orderDirection === 'down' && selectedIndex === 0) {
        // Given the above conditions and then moving the first element down, the scroll position is
        // changed so that there might be enough space for the sticky header to disappear, leading to
        // jumping controls and bad UX. The following scrolls the parent in a way that the first and
        // second item change their positions without affecting the display of the sticky elements.
        scrollParent.scrollBy({
          top: Math.abs(stickyBorderBottom - selectedRowRect.y) / 2,
        });
      }

      if (isChromiumBrowser) {
        // Fix for Chrome: scrollIntoView and behavior: 'smooth' is buggy in Chrome. (2025.02.10)
        if (selectedRowRect.y < stickyBorderBottom) {
          scrollParent.scrollBy({
            top: selectedRowRect.y - stickyBorderBottom,
          });
        }
      }
    }

    // Works for FF in both directions and for Chrome when scrolling down (there are no sticky elements at the bottom)
    // Set behavior: 'instant' for Chrome so that it's the same for moving items up and down (see Chrome fix above).
    scrollIntoView(selectedRow, { block: 'nearest', behavior: isChromiumBrowser ? 'instant' : 'smooth' });
    selectedRow.style.scrollMarginTop = '';

    if (orderDirection === 'up') {
      const nextAriaIndex = nextRow.getAttribute('aria-rowindex');
      nextRow.setAttribute('aria-rowindex', String(selectedRow.getAttribute('aria-rowindex')));
      selectedRow.setAttribute('aria-rowindex', String(nextAriaIndex));
    } else {
      const previousItem = selectedRow.previousElementSibling;
      assert(previousItem, 'previousItem must exist.');
      const previousAriaIndex = previousItem.getAttribute('aria-rowindex');
      previousItem.setAttribute('aria-rowindex', String(selectedRow.getAttribute('aria-rowindex')));
      selectedRow.setAttribute('aria-rowindex', String(previousAriaIndex));
    }
  }

  /**
   * When ordering table items manually, it should not reload its data after a change in the
   * order has been made (UX). Therefore, the items need to be reordered accordingly on the client.
   */
  #moveRows({ nextRow, rowsToMove }: MoveToData) {
    const allRowElements = this.rowElements;

    if (nextRow) {
      nextRow.before(...rowsToMove);
    } else {
      requireNonNullable(allRowElements.at(-1)).after(...rowsToMove);
    }

    // We do not allow pagination enabled together with manual movement, but as we always set aria-rowindex attributes,
    // we always have to ensure its correctness. For simplicity, we update all rendered rows
    for (const rowElement of allRowElements) {
      rowElement.setAttribute('aria-rowindex', String(rowElement.rowIndex + 1));
    }
  }

  #canReorder(selectedIndex: number) {
    const allRows = this.rowElements;
    const rowsAreFiltered = this.rowCount !== this.rowCountWithoutFilter;
    const upDisabled =
      rowsAreFiltered ||
      !allRows[selectedIndex - 1] ||
      allRows[selectedIndex - 1]?.classList.contains(ROW_GROUP_HEADER_CLASS);
    const downDisabled =
      rowsAreFiltered ||
      !allRows[selectedIndex + 1] ||
      allRows[selectedIndex + 1]?.classList.contains(ROW_GROUP_HEADER_CLASS);
    return {
      upDisabled,
      downDisabled,
    };
  }

  #assertManualOrderApplicable(
    columns: Column<T>[],
    pagination: boolean,
    manualOrderingConfig?: ToolbarManualOrderingConfig<T>,
  ) {
    if (!manualOrderingConfig) {
      return;
    }
    const hasSortableColumn = columns.some(
      (column) => column.sort !== undefined && (column.sort === true || column.sort !== 'disabled'),
    );
    if (hasSortableColumn) {
      throw new Error('Manual ordering and sortable columns are not compatible.');
    }
    if (pagination) {
      throw new Error('Manual ordering and pagination are not compatible.');
    }
  }

  #initializeSelection() {
    if (this.selectionMode === 'single') {
      on(
        this,
        'click',
        (event) => {
          const tr = event.delegateTarget as HTMLTableRowElement;
          if (isRowDisabled(tr)) {
            return;
          }
          const target: Node = event.target as Node;
          const wasClickOnRow = getTabbables(event.delegateTarget as HTMLElement).every(
            (tabbable) => tabbable === event.delegateTarget || !tabbable.contains(target),
          );
          if (wasClickOnRow) {
            const previouslySelectedTr = this.#tBody.querySelector<HTMLTableRowElement>('tr[aria-selected=true]');
            if (previouslySelectedTr && previouslySelectedTr !== tr) {
              setRowSelected(previouslySelectedTr, false);
            }
            setRowSelected(tr, !isRowSelected(tr));
            this.dispatchEvent(new CustomEvent(TableEvents.SELECT));
          }
        },
        { delegate: `tbody .${ROW_CLASS}` },
      );
    }

    if (this.selectionMode === 'multiple') {
      const selectionHint = createSelectionHint();
      this.#selectionHeaderCheckbox = createSelectionHeaderCheckbox();

      this.#columns.unshift({
        name: [selectionHint, this.#selectionHeaderCheckbox],
        key: 'selectRow' as ColumnKey<T>,
        width: isTest() ? '24px' : getComputedStyle(document.body).getPropertyValue('--small-component-size'),
        renderer: (_, { tr, rowHeadingId }) => {
          return renderSelectionCellContent(
            rowHeadingId,
            selectionHint.id,
            isRowDisabled(tr), // Disable checkbox
          );
        },
      } satisfies Column<T>);

      on(this.#selectionHeaderCheckbox, 'change', () => {
        const isChecked = this.#selectionHeaderCheckbox!.checked;
        const enabledTableRows = this.rowElements.filter((tr) => !isRowDisabled(tr));
        if (enabledTableRows.length) {
          // Should be true, otherwise the HeaderCheckbox will be disabled
          for (const row of enabledTableRows) {
            const selectionCheckbox = row.querySelector<HTMLInputElement>(`.${SELECTION_CHECKBOX_CLASS}`);
            if (selectionCheckbox) {
              setRowSelected(row, isChecked);
              selectionCheckbox.checked = isChecked;
            }
          }
          this.dispatchEvent(new CustomEvent(TableEvents.SELECT));
        }
      });

      on(
        this,
        'change',
        (event) => {
          const selectionCheckbox = event.delegateTarget as HTMLInputElement;
          const tr = selectionCheckbox.closest('tr')!;
          if (!isRowDisabled(tr)) {
            setRowSelected(tr, selectionCheckbox.checked); // Selection checkbox is always in a <tr> element
            this.dispatchEvent(new CustomEvent(TableEvents.SELECT));
            this.#updateSelectionHeaderCheckbox();
          }
        },
        { delegate: `tbody .${SELECTION_CHECKBOX_CLASS}` },
      );
    }
  }
}

customElements.define('d-responsive-table', ResponsiveTable);

function setRowSelected(rowElement: HTMLTableRowElement, selected: boolean) {
  assert(!isRowDisabled(rowElement) || !selected, 'A non-selectable table row was selected.'); // A use case seems unlikely
  rowElement.setAttribute('aria-selected', String(selected));
  toggleDarkLight(rowElement, selected);
  const trCheckbox = rowElement.querySelector<HTMLInputElement>(`.${SELECTION_CHECKBOX_CLASS}`);
  if (trCheckbox) {
    // Assumes that we are in selection mode multiple
    trCheckbox.checked = selected;
  }
}

function isRowSelected(rowElement: HTMLTableRowElement): boolean {
  return rowElement.getAttribute('aria-selected') === 'true';
}

function isRowDisabled(rowElement: HTMLTableRowElement): boolean {
  return rowElement.getAttribute('aria-disabled') === 'true';
}

/**
 * The default comparator function
 *
 * Uses locale-specific ordering for strings and natural ordering for other types.
 *
 * @type {Comparator}
 */
export function defaultCompare(a: unknown, b: unknown, order: SortOrder<unknown>['order'] = 'ascending') {
  return defaultCompareInternal(a, b) * (order === 'ascending' ? 1 : -1);
}

function defaultCompareInternal(a: unknown, b: unknown) {
  if (typeof a === 'string' || typeof b === 'string') {
    return compareWithLocale(String(a ?? ''), String(b ?? ''));
  }

  // natural ordering
  // @ts-expect-error -- TS doesn't allow natural ordering of unknown types
  if (a > b) {
    return 1;
  }
  // @ts-expect-error -- TS doesn't allow natural ordering of unknown types
  if (b > a) {
    return -1;
  }

  return 0; // if both are the same
}

function renderSelectionCellContent(rowHeadingId: string, selectionHintId: string, disabled?: boolean) {
  return h(`input.${SELECTION_CHECKBOX_CLASS}`, {
    type: 'checkbox',
    title: i18n('selection.one'),
    // https://adrianroselli.com/2019/05/uniquely-labeling-fields-in-a-table.html#Update01
    attrs: { 'aria-labelledby': `${rowHeadingId} ${selectionHintId}` },
    disabled,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function assertColumnWidths(columns: Column<any>[]) {
  columns.forEach((column) => {
    const { width, resizingRequiredMinWidth } = column;
    assert(
      width === undefined ||
        (typeof width === 'string' && IS_FIXED_WIDTH_IN_PX_CHECKER.test(width)) ||
        Number.isInteger(width),
      'If defined, column width must be defined as whole number or string defining css length in px (e.g. 100px)',
    );
    assert(
      resizingRequiredMinWidth === undefined || IS_FIXED_WIDTH_IN_PX_CHECKER.test(resizingRequiredMinWidth),
      'If defined, column minWidth must be defined as string defining css length in px (e.g. 100px)',
    );
  });
}

function findScrollParent(node: Element) {
  let parent = node.parentElement;
  const scrollStyles = ['overflow', 'overflow-y'];
  const scrollingValues = ['auto', 'scroll'];
  while (parent) {
    const parentStyle = getComputedStyle(parent);
    for (const style of scrollStyles) {
      for (const val of scrollingValues) {
        if (parentStyle.getPropertyValue(style) === val) {
          return parent;
        }
      }
    }
    parent = parent.parentElement;
  }
  // fallback
  return document.body;
}

function getColumnSort<T>({ sort, main }: Column<T>) {
  return sort ?? (main ? 'ascending' : false);
}

function isSortable(sort: boolean | ColumnSort) {
  return sort && sort !== 'disabled';
}

function isColumnSortable<T>(column: Column<T>) {
  return isSortable(getColumnSort(column));
}
